<script lang="ts" setup>
    import BaseButton from '@/components/BaseButton.vue';
    import BaseIcon from '@/components/BaseIcon.vue';
    import { useConfigStore } from '@/store/config.store';
    import { useTemplateStore } from '@/store/template.store';
    import { Events } from '@/services/Events';
    import { useField } from '@/composables/useField';
    import IconZoomIn from '@/components/icons/IconZoomIn.vue';
    import AlertImage from '@/components/AlertImage.vue';
    import { useZoomControl } from '@/composables/useZoomControl';
    import { EProductFormat } from '@/typescript/enums/EProductFormat';
    import { dataStore } from '@/commons/DataStore';
    import { initCanvas } from '@/littleWedding/canvasBuilder';
    import { isFieldOnSide } from '@/utils/fieldSide';
    import { storeToRefs } from 'pinia';
    import { computed, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';

    const { format = 'recto' } = defineProps<{
        format?: 'recto' | 'verso';
    }>();

    const { zoomHome, zoomIn, zoomOut } = useZoomControl();
    const { currentSide, displayGrid, isMobile, isProductEdition, printHeight, printWidth, productFormat, productName } =
        storeToRefs(useConfigStore());
    const { selectedFieldId } = storeToRefs(useTemplateStore());
    const { addFieldToCanvas, deleteFieldInsideCanvas, filteredFields, updateFieldsAndOrder } = useField();
    let updateMaterial: () => void;
    const qualityWarning = ref(false);

    const fabricElementRef = useTemplateRef<HTMLCanvasElement | null>(`fabricElementRef-${format}`);

    const shouldDisplayZoomButtons = computed(() => !isMobile.value || !selectedFieldId.value);

    const formatedProductLabel = computed(
        () => `${productName.value} - ${formatedProductFormat.value} - ${printHeight.value / 10} X ${printWidth.value / 10}CM`,
    );
    const formatedProductFormat = computed(() => (productFormat.value === EProductFormat.RECTO_VERSO ? 'RECTO/VERSO' : 'RECTO'));

    onMounted(async () => {
        updateMaterial = await initCanvas(fabricElementRef, format);
        Events.on('field-selected', selectedFieldCallback);
        Events.on('image-quality-warning', qualityCheckCallback);
    });

    onUnmounted(() => {
        selectedFieldCallback(undefined);
        Events.off('field-selected', selectedFieldCallback);
        Events.off('image-quality-warning', qualityCheckCallback);
        window.removeEventListener('load', () => initCanvas(fabricElementRef, format));
    });

    watch(filteredFields, (newValues, oldValues) => {
        const fabricWorld = dataStore.getFabricWorld(format);
        const oldFilteredBySide = oldValues.filter((f) => isFieldOnSide(f, format));
        const newFilteredBySide = newValues.filter((f) => isFieldOnSide(f, format));
        if (newFilteredBySide.length === oldFilteredBySide.length) {
            updateFieldsAndOrder(fabricWorld, newFilteredBySide);
            return;
        }
        if (newFilteredBySide.length < oldFilteredBySide.length) {
            deleteFieldInsideCanvas(fabricWorld, oldFilteredBySide, newFilteredBySide);
            return;
        }
        const diffField = newFilteredBySide.filter((newField) => !oldFilteredBySide.some((oldField) => oldField.id === newField.id));
        if (diffField.length === 0) {
            return;
        }
        addFieldToCanvas(fabricWorld, diffField[0], updateMaterial);
    });

    watch(displayGrid, (newValue) => {
        dataStore.getFabricWorld(currentSide.value).setGridHelperVisibility(newValue);
    });

    function selectedFieldCallback(fieldId: string | undefined) {
        selectedFieldId.value = fieldId;
    }

    function qualityCheckCallback(args: { hasQualityWarning: boolean }) {
        qualityWarning.value = args.hasQualityWarning;
    }
</script>

<template>
    <div :class="[isMobile && 'h-dvh w-full', !isMobile && 'h-full', 'bg-bg border-border absolute flex grow overflow-hidden border']">
        <AlertImage :show-alert="qualityWarning" />
        <div
            v-if="isProductEdition"
            class="pointer-events-none absolute top-8 z-20 w-full text-center text-xl font-medium uppercase"
        >
            {{ formatedProductLabel }}
        </div>
        <canvas
            :ref="`fabricElementRef-${format}`"
            :class="[isMobile && 'h-dvh', !isMobile && 'h-full', 'absolute']"
        />
        <div
            v-if="shouldDisplayZoomButtons"
            :class="[
                'z-10 flex gap-3',
                !isMobile && 'absolute bottom-12 right-2 mr-8 flex-col',
                isMobile && 'fixed bottom-28 left-1/2 -translate-x-1/2 flex-row',
            ]"
        >
            <BaseButton
                class="pointer-events-auto h-9 w-9"
                @click="zoomHome"
            >
                <span class="sr-only">
                    {{ $t('zoom') }}
                </span>
                <IconZoomIn />
            </BaseButton>
            <BaseButton
                class="pointer-events-auto h-9 w-9"
                @click="zoomIn"
            >
                <span class="sr-only">
                    {{ $t('zoom_in') }}
                </span>
                <BaseIcon
                    size="lg"
                    name="MagnifyingGlassPlusIcon"
                />
            </BaseButton>
            <BaseButton
                class="pointer-events-auto h-9 w-9"
                @click="zoomOut"
            >
                <span class="sr-only">
                    {{ $t('zoom_out') }}
                </span>
                <BaseIcon
                    size="lg"
                    name="MagnifyingGlassMinusIcon"
                />
            </BaseButton>
        </div>
    </div>
</template>
